import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import {
  canApplyForJobs,
  isHiringAcceptingApplications,
} from "../utils/userRole";

const API_URL = getApiBaseUrl();

const fieldStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  marginBottom: 12,
  outline: "none",
  boxSizing: "border-box",
  background: "#f8fafc",
  color: "#0f172a",
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 6,
};

function displayName(user) {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.username || "";
}

/**
 * In-app job application modal for User / Intern accounts.
 */
export default function JobApplicationForm({
  user,
  post,
  open,
  onClose,
  onSubmitted,
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [reuseResumeId, setReuseResumeId] = useState("");
  const [priorResumes, setPriorResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const jobTitle = post?.title || "this role";
  const maxMb = 8;

  useEffect(() => {
    if (!open || !user) return;
    setFullName(displayName(user));
    setEmail(user.email || "");
    setMobile("");
    setCoverLetter("");
    setPortfolioUrl("");
    setResumeFile(null);
    setReuseResumeId("");
    setError("");
    setSuccess(false);
    setLoading(false);

    // Load prior resumes for reuse
    const loadPriors = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/get_my_applications.php?applicant_id=${user.id}`
        );
        if (res.data?.success) {
          const withResume = (res.data.applications || []).filter(
            (a) => a.has_resume && a.status !== "withdrawn"
          );
          // Dedupe by original name + keep latest
          const seen = new Set();
          const list = [];
          for (const a of withResume) {
            const key = a.resume_original_name || a.id;
            if (seen.has(key)) continue;
            seen.add(key);
            list.push(a);
            if (list.length >= 5) break;
          }
          setPriorResumes(list);
        }
      } catch {
        setPriorResumes([]);
      }
    };
    loadPriors();
  }, [open, user]);

  const canSubmit = useMemo(() => {
    if (!canApplyForJobs(user)) return false;
    if (!fullName.trim() || !email.trim() || !mobile.trim()) return false;
    if (!resumeFile && !reuseResumeId) return false;
    return true;
  }, [user, fullName, email, mobile, resumeFile, reuseResumeId]);

  if (!open) return null;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      setError("Resume must be PDF, DOC, or DOCX.");
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setError(`Resume must be under ${maxMb} MB.`);
      return;
    }
    setError("");
    setResumeFile(file);
    setReuseResumeId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    if (!isHiringAcceptingApplications(post)) {
      setError(
        post?.hiring_closed
          ? "This job posting has been closed by the employer."
          : "The application deadline for this job has passed."
      );
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("post_id", String(post.id));
      formData.append("applicant_id", String(user.id));
      formData.append("full_name", fullName.trim());
      formData.append("email", email.trim());
      formData.append("mobile", mobile.trim());
      formData.append("cover_letter", coverLetter.trim());
      formData.append("portfolio_url", portfolioUrl.trim());
      if (resumeFile) {
        formData.append("resume", resumeFile, resumeFile.name);
      } else if (reuseResumeId) {
        formData.append("reuse_resume_application_id", String(reuseResumeId));
      }

      // Do not set Content-Type manually — the browser must add the multipart boundary.
      const res = await axios.post(
        `${API_URL}/submit_job_application.php`,
        formData
      );
      if (!res.data?.success) {
        setError(res.data?.message || "Failed to submit application.");
        return;
      }
      setSuccess(true);
      if (typeof onSubmitted === "function") {
        onSubmitted(res.data.application);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Network error submitting application."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 4000,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "92vh",
          overflowY: "auto",
          background: "#fff",
          borderRadius: 16,
          padding: "22px 22px 18px",
          boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "#0f766e",
                marginBottom: 4,
              }}
            >
              Apply
            </div>
            <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>
              {jobTitle}
            </h2>
            {post?.hiring_company && (
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                {post.hiring_company}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              color: "#64748b",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {success ? (
          <div style={{ textAlign: "center", padding: "24px 8px" }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "#ccfbf1",
                color: "#0f766e",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              ✓
            </div>
            <h3 style={{ margin: "0 0 8px", color: "#0f172a" }}>
              Application submitted
            </h3>
            <p style={{ margin: "0 0 18px", color: "#64748b", fontSize: 14 }}>
              The employer has been notified. You can track status under My
              Applications.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "none",
                background: "#0f766e",
                color: "#fff",
                fontWeight: 650,
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={labelStyle}>Full Name</label>
            <input
              style={fieldStyle}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />

            <label style={labelStyle}>Email</label>
            <input
              style={fieldStyle}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label style={labelStyle}>Mobile Number</label>
            <input
              style={fieldStyle}
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+63..."
              required
            />

            <label style={labelStyle}>Cover Letter (optional)</label>
            <textarea
              style={{ ...fieldStyle, minHeight: 90, resize: "vertical" }}
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Briefly introduce yourself and why you're a fit..."
            />

            <label style={labelStyle}>Portfolio or LinkedIn URL (optional)</label>
            <input
              style={fieldStyle}
              type="url"
              value={portfolioUrl}
              onChange={(e) => setPortfolioUrl(e.target.value)}
              placeholder="https://"
            />

            <label style={labelStyle}>Resume / CV (PDF, DOC, DOCX · max {maxMb} MB)</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFile}
              style={{ marginBottom: 8 }}
            />
            {resumeFile && (
              <div style={{ fontSize: 13, color: "#0f766e", marginBottom: 10 }}>
                Selected: {resumeFile.name}
              </div>
            )}

            {priorResumes.length > 0 && (
              <>
                <label style={labelStyle}>Or reuse a previous resume</label>
                <select
                  style={fieldStyle}
                  value={reuseResumeId}
                  onChange={(e) => {
                    setReuseResumeId(e.target.value);
                    if (e.target.value) setResumeFile(null);
                  }}
                >
                  <option value="">— Upload new file instead —</option>
                  {priorResumes.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.resume_original_name || `Application #${a.id}`} ·{" "}
                      {a.job_title || "previous"}
                    </option>
                  ))}
                </select>
              </>
            )}

            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  color: "#b91c1c",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || loading}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "none",
                  background: canSubmit && !loading ? "#0f766e" : "#94a3b8",
                  color: "#fff",
                  fontWeight: 650,
                  cursor: canSubmit && !loading ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
